import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Save, User, FileText, Eye, Upload, Check, Lock } from 'lucide-react';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import EmployerSubmissions from '../components/EmployerSubmissions';
import { getDocLabel } from '../lib/docLabels';

export default function ProfileSettings() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [previewDoc, setPreviewDoc] = useState(null);
    // Replace flow: { file, docType, label } | null
    const [pendingReplace, setPendingReplace] = useState(null);
    const [replacing, setReplacing] = useState(false);
    const [replaceSuccess, setReplaceSuccess] = useState('');
    // One hidden <input type="file"> per doc row, keyed by doc.id
    const fileInputs = useRef({});

    // Common fields
    const [name, setName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [clinicName, setClinicName] = useState('');

    // Worker fields
    const [dob, setDob] = useState('');
    const [country, setCountry] = useState('');
    const [interests, setInterests] = useState('');
    const [languages, setLanguages] = useState('');
    const [experience, setExperience] = useState('');
    const [bio, setBio] = useState('');
    const [phone, setPhone] = useState('');

    // Password
    const [newPassword, setNewPassword] = useState('');

    const loadDocuments = useCallback(async () => {
        if (user?.role === 'worker') {
            try {
                const { data } = await api.get('/worker/documents');
                setDocuments(data.documents);
            } catch (err) {
                console.error('ProfileSettings: failed to load worker documents', err);
            }
        }
    }, [user?.role]);

    useEffect(() => {
        if (!user) return;
        setName(user.name || '');
        setCompanyName(user.company_name || '');
        setClinicName(user.clinic_name || '');
        setDob(user.dob || '');
        setCountry(user.country || '');
        setInterests((user.interests || []).join(', '));
        setLanguages((user.languages || []).join(', '));
        setExperience(user.experience || '');
        setBio(user.bio || '');
        setPhone(user.phone || '');
        loadDocuments();
    }, [user, loadDocuments]);

    const saveProfile = async () => {
        setSaving(true);
        setSaved(false);
        try {
            if (user.role === 'worker') {
                await api.put('/worker/profile', {
                    name, dob, country,
                    interests: interests.split(',').map(s => s.trim()).filter(Boolean),
                    languages: languages.split(',').map(s => s.trim()).filter(Boolean),
                    experience, phone, bio
                });
            } else if (user.role === 'employer') {
                await api.put('/employer/profile', { company_name: companyName, name });
            } else if (user.role === 'medical') {
                await api.put('/medical/profile', { clinic_name: clinicName, name });
            }
            await refreshUser();
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const viewDocument = (doc) => {
        setPreviewDoc(doc);
    };

    const onPickReplacementFile = (e, doc) => {
        const file = e.target.files && e.target.files[0];
        // Reset the input so picking the same filename twice still fires onChange
        e.target.value = '';
        if (!file) return;
        setPendingReplace({
            file,
            docType: doc.doc_type,
            label: getDocLabel(doc),
        });
    };

    const confirmReplaceDocument = async () => {
        if (!pendingReplace) return;
        const { file, docType, label } = pendingReplace;
        setReplacing(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', docType);
            await api.post('/worker/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            await loadDocuments();
            setReplaceSuccess(`${label} updated successfully.`);
            setTimeout(() => setReplaceSuccess(''), 3500);
        } catch (err) {
            console.error('ProfileSettings: replace failed', err);
        } finally {
            setReplacing(false);
            setPendingReplace(null);
        }
    };

    if (!user) return null;

    return (
        <div className="max-w-xl mx-auto" data-testid="profile-settings-page">
            <div className="flex items-center gap-3 mb-6">
                <button onClick={() => navigate(`/${user.role}`)} className="text-zinc-400 hover:text-zinc-900" data-testid="back-to-dashboard-btn">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="font-heading text-2xl font-bold">Profile & Settings</h1>
            </div>

            {saved && (
                <div className="border border-green-200 bg-green-50 p-3 mb-4 flex items-center gap-2" data-testid="save-success">
                    <Check className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Changes saved successfully</span>
                </div>
            )}

            {replaceSuccess && (
                <div className="border border-green-200 bg-green-50 p-3 mb-4 flex items-center gap-2" data-testid="replace-success">
                    <Check className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">{replaceSuccess}</span>
                </div>
            )}

            {/* Signed-in identity strip — quick reminder of the email the user
                is currently logged in with. Purely informational, non-editable. */}
            <div
                className="border border-zinc-200 bg-zinc-50 px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                data-testid="signed-in-as-strip"
            >
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                    Signed in as
                </span>
                <span
                    className="text-sm font-semibold text-zinc-900 break-all"
                    data-testid="signed-in-as-email"
                >
                    {user.email}
                </span>
            </div>

            <div className="space-y-6">
                {/* Worker Profile Fields */}
                {user.role === 'worker' && (
                    <div className="border border-zinc-200 p-6 space-y-4" data-testid="worker-profile-fields">
                        <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase tracking-wide">Personal Information</h3>
                        <div><Label className="text-sm font-semibold">Full Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-name" /></div>
                        <div><Label className="text-sm font-semibold">Date of Birth</Label><Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-dob" /></div>
                        <div><Label className="text-sm font-semibold">Country</Label><Input value={country} onChange={e => setCountry(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-country" /></div>
                        <div><Label className="text-sm font-semibold">Interests (comma-separated)</Label><Input value={interests} onChange={e => setInterests(e.target.value)} className="mt-1 h-12 rounded-none" placeholder="Security, Driver, Cleaner" data-testid="settings-interests" /></div>
                        <div><Label className="text-sm font-semibold">Languages (comma-separated)</Label><Input value={languages} onChange={e => setLanguages(e.target.value)} className="mt-1 h-12 rounded-none" placeholder="English, Arabic" data-testid="settings-languages" /></div>
                        <div><Label className="text-sm font-semibold">Experience</Label><Input value={experience} onChange={e => setExperience(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-experience" /></div>
                        <div><Label className="text-sm font-semibold">Bio</Label><Input value={bio} onChange={e => setBio(e.target.value)} className="mt-1 h-12 rounded-none" placeholder="A short bio about yourself" data-testid="settings-bio" /></div>
                        <div><Label className="text-sm font-semibold">Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-phone" /></div>
                    </div>
                )}

                {/* Employer Fields */}
                {user.role === 'employer' && (
                    <div className="border border-zinc-200 p-6 space-y-4" data-testid="employer-profile-fields">
                        <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase tracking-wide">Company Information</h3>
                        <div><Label className="text-sm font-semibold">Company Name</Label><Input value={companyName} onChange={e => setCompanyName(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-company" /></div>
                        <div><Label className="text-sm font-semibold">Contact Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-name" /></div>
                    </div>
                )}

                {/* Employer-only: reimbursement claim submissions */}
                {user.role === 'employer' && (
                    <EmployerSubmissions api={api} user={user} />
                )}

                {/* Medical Partner Fields */}
                {user.role === 'medical' && (
                    <div className="border border-zinc-200 p-6 space-y-4" data-testid="medical-profile-fields">
                        <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase tracking-wide">Clinic Information</h3>
                        <div><Label className="text-sm font-semibold">Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-name" /></div>
                        <div><Label className="text-sm font-semibold">Clinic Name</Label><Input value={clinicName} onChange={e => setClinicName(e.target.value)} className="mt-1 h-12 rounded-none" data-testid="settings-clinic" /></div>
                    </div>
                )}

                {/* Documents (Worker only) */}
                {user.role === 'worker' && documents.length > 0 && (
                    <div className="border border-zinc-200 p-6" data-testid="settings-documents">
                        <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase tracking-wide mb-4">Your Documents</h3>
                        <div className="space-y-2">
                            {documents.map(doc => {
                                const isLocked = !!doc.locked;
                                const rowKey = doc.id || `locked-${doc.doc_type}`;
                                return (
                                <div key={rowKey} className={`flex items-center justify-between p-3 border ${isLocked ? 'border-amber-200 bg-amber-50' : 'border-zinc-100'}`} data-testid={`settings-doc-row-${doc.doc_type}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        {isLocked ? (
                                            <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                        ) : (
                                            <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-sm capitalize truncate">{getDocLabel(doc)}</p>
                                            {isLocked ? (
                                                <p className="text-[10px] text-amber-700 truncate font-semibold">Complete payment to access</p>
                                            ) : (
                                                <p className="text-[10px] text-zinc-400 truncate">{doc.original_filename}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        {isLocked ? (
                                            <Button
                                                size="sm"
                                                className="rounded-none text-xs h-7 bg-zinc-900 text-white"
                                                onClick={() => navigate('/worker?pay=1')}
                                                data-testid={`unlock-doc-${doc.doc_type}`}
                                            >
                                                <Lock className="w-3 h-3 mr-1" /> Unlock
                                            </Button>
                                        ) : (
                                            <>
                                                <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" onClick={() => viewDocument(doc)} data-testid={`settings-view-${doc.id}`}><Eye className="w-3 h-3" /></Button>
                                                {doc.doc_type !== 'medical_report' && doc.doc_type !== 'visa' && doc.doc_type !== 'flight_ticket' && (
                                                    <>
                                                        <input
                                                            ref={el => { fileInputs.current[doc.id] = el; }}
                                                            type="file"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            className="hidden"
                                                            onChange={(e) => onPickReplacementFile(e, doc)}
                                                            data-testid={`settings-replace-input-${doc.doc_type}`}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="rounded-none text-xs h-7"
                                                            onClick={() => fileInputs.current[doc.id] && fileInputs.current[doc.id].click()}
                                                            data-testid={`settings-replace-${doc.doc_type}`}
                                                            title="Replace document"
                                                        >
                                                            <Upload className="w-3 h-3" />
                                                        </Button>
                                                    </>
                                                )}
                                                {doc.doc_type === 'medical_report' && <Badge variant="outline" className="rounded-none text-[10px]">Read-only</Badge>}
                                            </>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Save Button */}
                <Button onClick={saveProfile} disabled={saving} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid="save-settings-btn">
                    {saving ? 'Saving...' : <><Save className="w-5 h-5 mr-2" /> Save Changes</>}
                </Button>
            </div>

            <DocumentPreviewModal
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                doc={previewDoc}
            />

            <AlertDialog open={!!pendingReplace} onOpenChange={(o) => { if (!o && !replacing) setPendingReplace(null); }}>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900" data-testid="replace-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Replace your current {pendingReplace?.label}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Replace your current {pendingReplace?.label} with the new file? This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={replacing} className="rounded-none" data-testid="replace-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={replacing}
                            className="rounded-none bg-zinc-900 text-white"
                            onClick={(e) => { e.preventDefault(); confirmReplaceDocument(); }}
                            data-testid="replace-confirm-btn"
                        >
                            {replacing ? 'Replacing…' : 'Confirm'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
